import { useState } from "react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import {
    Plus,
    Trash2,
    SquarePen,
} from "lucide-react";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { AlertDialog } from "@/widgets/AlertDailog";
import { PaginationControl } from "@/widgets/renderPagination";
import { useLocation } from "wouter";
import { Popup } from "@/widgets/RuleDetailDailog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { deleteWorkflowTeam, useWorkflowPerformerTeams } from "@/hooks/useLocalStorage";
import { WorkflowTeamsRes } from "@/lib/api-services";
import { DeleteConfirmationDialog } from "@/widgets/DeleteModal";
import { parseApiErrorMessage } from "@/hooks/parseApiErrorMessage";
import { Input } from "../ui/input";
import { useBreadcrumbs } from "../BreadcrumbContext";
import { AddWorkflowPerformerTeam } from "./AddWorkflowPerformerTeam";


interface TableHierarchicalNodeProps {
    topLevelData: WorkflowTeamsRes[];
    pageIndex: number;
    pageSize: number;
    totalCount: number;
    onPageChange: (page: number) => void;
    onEditForm: (data: any) => void;
}

function TableForm({
    topLevelData,
    pageIndex,
    pageSize,
    totalCount,
    onPageChange,
    onEditForm,
}: TableHierarchicalNodeProps) {
    const [currentData, setCurrentData] = useState<WorkflowTeamsRes[]>([]);
    const [alertOpen, setAlertOpen] = React.useState(false);
    const [alertMessage, setAlertMessage] = React.useState("");
    const [reqStatus, setReqStatus] = React.useState(false);

    const [showPopup, setShowPopup] = React.useState(false);
    const [popupName, setPopupName] = React.useState("");
    const [headingData, setHeadingData] = React.useState<string | null>("");
    const [descriptionContent, setDescriptionContent] = React.useState<string | null>("");

    const [_location, setLocation] = useLocation();

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<WorkflowTeamsRes | null>(null);

    const deletePortalTeam = deleteWorkflowTeam();

    React.useEffect(() => {
        if (topLevelData) {
            setCurrentData(topLevelData);
        }
    }, [topLevelData]);


    function handleDeleteClick(e: React.MouseEvent, item: WorkflowTeamsRes) {
        e.stopPropagation();
        setItemToDelete(item);
        setDeleteDialogOpen(true);
    }

    function handleConfirmDelete() {
        if (!itemToDelete) return;
        deletePortalTeam.mutate(itemToDelete.id);
    }

    React.useEffect(() => {
        if (deletePortalTeam.isSuccess) {
            setDeleteDialogOpen(false);
            setReqStatus(true);
            setAlertMessage("Workflow Performer Team deleted successfully!");
            setItemToDelete(null);
            setAlertOpen(true);
            deletePortalTeam.reset();
        } else if (deletePortalTeam.isError) {
            const raw = deletePortalTeam.error as Error;
            setReqStatus(false);
            setDeleteDialogOpen(false);
            setAlertMessage(parseApiErrorMessage(raw.message));
            setAlertOpen(true);
            deletePortalTeam.reset();
        }
    }, [deletePortalTeam.isSuccess, deletePortalTeam.isError]);


    function handleViewDescription(item: WorkflowTeamsRes) {
        setPopupName("Description");
        setHeadingData(item.teamName)
        setDescriptionContent(item.teamDesc);
        setShowPopup(true);
    }


    return (
        <div>
            <div className="border bg-white border-gray-200 rounded-lg p-2">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Id</TableHead>
                            <TableHead>Team Name</TableHead>
                            <TableHead>Workflow Assignment</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead>Created Time</TableHead>
                            <TableHead>Last Modified</TableHead>
                            <TableHead>Actions</TableHead>
                        </TableRow>
                    </TableHeader>

                    <TableBody>
                        {currentData?.map((data) => (
                            <TableRow key={data.id}>
                                <TableCell className="font-mono">{data.id}</TableCell>
                                <TableCell className="font-mono">{data.teamName}</TableCell>
                                <TableCell className="font-mono">{data.workflowAssignment}</TableCell>

                                <TableCell className="max-w-xs truncate">
                                    <Button
                                        variant="link"
                                        onClick={() => handleViewDescription(data)}
                                    >
                                        View
                                    </Button>
                                </TableCell>

                                <TableCell className="max-w-xs">
                                    <div className="flex flex-col">
                                        <span>
                                            {new Date(data.createdAt).toLocaleDateString("en-IN", {
                                                day: "2-digit",
                                                month: "short",
                                                year: "numeric",
                                            })}
                                        </span>
                                        <span className="text-xs text-black">
                                            {new Date(data.createdAt).toLocaleTimeString("en-IN", {
                                                hour: "2-digit",
                                                minute: "2-digit",
                                            })}
                                        </span>
                                    </div>
                                </TableCell>

                                <TableCell className="max-w-xs">
                                    <div className="flex flex-col">
                                        {data.lastModified ? (
                                            <>
                                                <span>
                                                    {new Date(data.lastModified).toLocaleDateString("en-IN", {
                                                        day: "2-digit", month: "short", year: "numeric",
                                                    })}
                                                </span>
                                                <span className="text-xs text-black">
                                                    {new Date(data.lastModified).toLocaleTimeString("en-IN", {
                                                        hour: "2-digit", minute: "2-digit",
                                                    })}
                                                </span>
                                            </>
                                        ) : "-"}
                                    </div>
                                </TableCell>

                                <TableCell>
                                    <div className="flex flex-row items-center space-x-2">
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="min-w-6 h-6 p-0"
                                                    tooltip="More actions"
                                                >
                                                    <Plus className="h-3 w-3" />
                                                    <span className="sr-only">More actions</span>
                                                </Button>
                                            </DropdownMenuTrigger>

                                            <DropdownMenuContent align="end" className="min-w-[180px]">

                                                <DropdownMenuItem
                                                    className="flex items-center px-1 cursor-pointer"
                                                    onClick={() => onEditForm(data)}
                                                >
                                                    <SquarePen className="h-5 w-5 mr-2 stroke-[2.5]" />
                                                    <span className="text-sm">Edit Form</span>
                                                </DropdownMenuItem>

                                                <DropdownMenuItem
                                                    onClick={(e) => handleDeleteClick(e, data)}
                                                    className="flex items-center px-1 cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
                                                >
                                                    <Trash2 className="h-5 w-5 mr-2 stroke-[2.5]" />
                                                    <span className="text-sm">Delete</span>
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                <div className="mt-2">
                    <PaginationControl
                        pageIndex={pageIndex}
                        pageSize={pageSize}
                        totalCount={totalCount}
                        onPageChange={onPageChange}
                    />
                </div>
            </div>

            <AlertDialog
                open={alertOpen}
                onOpenChange={setAlertOpen}
                message={alertMessage}
                status={reqStatus}
            />

            {showPopup && (
                <Popup
                    popupName={popupName}
                    heading={headingData}
                    content={descriptionContent}
                    onClose={() => setShowPopup(false)}
                />
            )}

            <DeleteConfirmationDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
                onConfirm={handleConfirmDelete}
                isPending={deletePortalTeam.isPending}
                itemName={itemToDelete as any}
                itemType="Workflow Performer Team"
                requireAuthCode={false}
            />

        </div>
    );
}


export function WorkflowPerformerTeam() {
    const { setBreadcrumbs } = useBreadcrumbs();
    const testdata = [
        {
            "id": 7,
            "teamName": "Team Name2",
            "teamDesc": "Team Description",
            "workflowAssignment": "direct_assign",
            "createdAt": "2026-04-22T12:21:49.683963",
            "lastModified": "2026-04-22T12:30:46.225018"
        },
    ]

    const [topLevelTypes, setTopLevelTypes] = useState<WorkflowTeamsRes[]>([]);
    const [pageIndex, setPageIndex] = useState(1);
    const [pageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    const [filterOpen, setFilterOpen] = useState(false);

    const [wfIdInput, setWfIdInput] = useState<string>("");
    const [wfNameInput, setWfNameInput] = useState<string>("");
    const [wfStatusInput, setWfStatusInput] = useState<string>("");

    const [appliedWfId, setAppliedWfId] = useState<string | undefined>();
    const [appliedWfName, setAppliedWfName] = useState<string | undefined>();
    const [appliedWfStatus, setAppliedWfStatus] = useState<string | undefined>();

    const [openCreateDialog, setOpenCreateDialog] = useState(false);
    const [editFormData, setEditFormData] = useState<any>(null);

    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [reqStatus, setReqStatus] = useState(false);

    const { data: types, isLoading } = useWorkflowPerformerTeams(
        pageIndex,
        pageSize,
        appliedWfId,
        appliedWfName,
        appliedWfStatus
    );

    React.useEffect(() => {
        setBreadcrumbs([{ label: "Performer Teams", path: `/workflowPerformer` }]);
    }, []);

    React.useEffect(() => {
        setTopLevelTypes(types?.items ?? []);
        setTotalCount(types?.total ?? 0);
    }, [types]);

    const handleSearch = () => {
        setAppliedWfId(wfIdInput.trim() || undefined);
        setAppliedWfName(wfNameInput.trim() || undefined);
        setAppliedWfStatus(wfStatusInput || undefined);
        setPageIndex(1);
    };

    const handleRefresh = (close: boolean) => {
        setWfIdInput("");
        setWfNameInput("");
        setWfStatusInput("");
        setAppliedWfId(undefined);
        setAppliedWfName(undefined);
        setAppliedWfStatus(undefined);
        setPageIndex(1);
        if (close) setFilterOpen(false);
    };

    const handleAddForm = () => {
        setEditFormData(null);
        setOpenCreateDialog(true);
    };

    const handleEditForm = (data: any) => {
        setEditFormData(data);
        setOpenCreateDialog(true);
    };

    const handleAlert = (message: string, status: boolean) => {
        setAlertMessage(message);
        setReqStatus(status);
        setAlertOpen(true);
    };

    return (
        <div className="min-h-full">

            <div className="mb-4 md:mb-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                    <div className="min-w-0">
                        <h2 className="text-lg md:text-xl font-semibold text-black">
                            Manage Performer Teams
                        </h2>
                        <p className="text-black mt-1 text-sm md:text-base">
                            Add or modify workflow performer team
                        </p>
                    </div>

                    <div className="flex items-center space-x-2 md:space-x-3 flex-shrink-0">
                        <Button
                            className="bg-workfinity-500 hover:bg-workfinity-600 text-sm md:text-base"
                            size="sm"
                            onClick={() => setFilterOpen((prev) => !prev)}
                        >
                            Filter Performer
                        </Button>
                        <Button
                            className="bg-workfinity-500 hover:bg-workfinity-600 text-sm md:text-base"
                            size="sm"
                            onClick={handleAddForm}
                        >
                            <Plus className="h-3 w-3 md:h-4 md:w-4 mr-2" />
                            Add Performer Team
                        </Button>
                    </div>
                </div>
            </div>

            {filterOpen && (
                <Card className="mb-4 md:mb-6">
                    <CardContent className="pt-6">
                        <div className="flex flex-col md:flex-row md:items-end md:space-x-4 space-y-3 md:space-y-0">
                            <div className="flex-1">
                                <label className="text-sm font-medium mb-1.5 block">Id</label>
                                <Input
                                    placeholder="Enter Id"
                                    value={wfIdInput}
                                    onChange={(e) => setWfIdInput(e.target.value)}
                                />
                            </div>

                            <div className="flex-1">
                                <label className="text-sm font-medium mb-1.5 block">Team Name</label>
                                <Input
                                    placeholder="Enter Role Name"
                                    value={wfNameInput}
                                    onChange={(e) => setWfNameInput(e.target.value)}
                                />
                            </div>

                        </div>

                        <div className="mt-4 flex justify-center space-x-2">
                            <Button onClick={handleSearch}>Search</Button>
                            <Button variant="outline" onClick={() => handleRefresh(false)}>
                                Refresh
                            </Button>
                            <Button variant="outline" onClick={() => handleRefresh(true)}>
                                Close
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}


            {!isLoading ? (
                <Card>
                    <CardContent className="p-4 md:p-6">
                        <div className="space-y-2">
                            {topLevelTypes && topLevelTypes.length > 0 ? (
                                <TableForm
                                    topLevelData={topLevelTypes}
                                    pageIndex={pageIndex}
                                    pageSize={pageSize}
                                    totalCount={totalCount}
                                    onPageChange={setPageIndex}
                                    onEditForm={handleEditForm}
                                />
                            ) : (
                                <div className="text-center py-8 text-black">
                                    <p>No Team found. Create one to get started.</p>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            ) : (
                <div className="p-6">Loading...</div>
            )}

            {
                openCreateDialog &&
                <AddWorkflowPerformerTeam
                    open={openCreateDialog}
                    onOpenChange={setOpenCreateDialog}
                    initialData={editFormData}
                    onAlert={handleAlert}
                />
            }

            <AlertDialog
                open={alertOpen}
                onOpenChange={setAlertOpen}
                message={alertMessage}
                status={reqStatus}
            />

        </div>
    );
}